import { useEffect, useState } from "react";
import { ShieldCheck, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

/**
 * Persistent banner shown when an admin has toggled "View as Mod" in JJT.
 * Appears only for users whose REAL role is admin (so true mods never see it).
 * Lets the admin one-click restore their admin view from anywhere in the app.
 */
export const ViewAsModBanner = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const recompute = async () => {
      const flag = typeof window !== "undefined" && localStorage.getItem("viewAsMod") === "1";
      if (!flag) { if (!cancelled) setShow(false); return; }
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { if (!cancelled) setShow(false); return; }
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();
      if (!cancelled) setShow(!!data && flag);
    };

    recompute();
    const onChange = () => recompute();
    const onStorage = (e: StorageEvent) => { if (e.key === "viewAsMod") recompute(); };
    const { data: sub } = supabase.auth.onAuthStateChange(() => recompute());
    window.addEventListener("viewAsMod-changed", onChange);
    window.addEventListener("storage", onStorage);
    return () => {
      cancelled = true;
      window.removeEventListener("viewAsMod-changed", onChange);
      window.removeEventListener("storage", onStorage);
      sub.subscription.unsubscribe();
    };
  }, []);

  if (!show) return null;

  const restore = () => {
    localStorage.removeItem("viewAsMod");
    window.dispatchEvent(new Event("viewAsMod-changed"));
    toast.success("Restored Admin view");
  };

  return (
    <div className="fixed top-2 left-1/2 -translate-x-1/2 z-[10000] pointer-events-auto">
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500 text-white shadow-lg text-xs font-semibold backdrop-blur">
        <ShieldCheck className="h-3.5 w-3.5" />
        <span>Viewing as Mod</span>
        <button
          onClick={restore}
          className="ml-1 flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/20 hover:bg-white/30 text-[11px] font-bold"
        >
          <X className="h-3 w-3" /> Exit
        </button>
      </div>
    </div>
  );
};
